import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { Plus, Search, Filter, FileText, RefreshCcw } from 'lucide-react';
import api from '../../services/api';
import { formatDate, getStatusText, getStatusColor, formatArea } from '../../utils/helpers';
import { useAuth } from '../../contexts/AuthContext';

const PermitList = () => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [appliedStatus, setAppliedStatus] = useState('');
  const [page, setPage] = useState(1);

  const { data, isLoading, isFetching, error } = useQuery(
    ['permits', page, appliedSearch, appliedStatus],
    async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '10'
      });

      if (appliedSearch) params.append('search', appliedSearch);
      if (appliedStatus) params.append('status', appliedStatus);

      const response = await api.get(`/permits?${params}`);
      return response.data;
    },
    {
      keepPreviousData: false,
      staleTime: 0,
      cacheTime: 0,
    },
  );

  const handleSearch = (e) => {
    e.preventDefault();
    setAppliedSearch(searchTerm);
    setAppliedStatus(statusFilter);
    setPage(1);
  };

  if (isLoading || isFetching) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <p className="text-red-800">Error loading permits: {error.message}</p>
      </div>
    );
  }

  const permits = data?.permits || [];
  const pagination = data?.pagination || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Permits</h1>
          <p className="text-secondary-600">Manage land permit applications</p>
        </div>
        <Link to="/permits/new" className="btn btn-primary">
          <Plus className="h-4 w-4 mr-2" />
          New Permit
        </Link>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-6">
        <form onSubmit={handleSearch} className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="label">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
              <input
                type="text"
                className="input pl-10"
                placeholder="Search by permit number, name, or address..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          
          <div>
            <label className="label">Status</label>
            <select
              className="input"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All Statuses</option>
              <option value="Draft">Draft</option>
              <option value="Pending_EAC_Review">Pending EAC Review</option>
              <option value="Pending_SDO_Review">Pending SDO Review</option>
              <option value="Pending_ADC_Review">Pending ADC Review</option>
              <option value="Pending_DC_Approval">Pending DC Approval</option>
              <option value="Active">Active</option>
              <option value="Cancelled">Cancelled</option>
              <option value="Transferred">Transferred</option>
              <option value="Renewal_Needed">Renewal Required</option>
              <option value="Returned">Returned</option>
            </select>
          </div>
          
          <div className="flex items-end">
            <button type="submit" className="btn btn-primary">
              <Filter className="h-4 w-4 mr-2" />
              Apply Filter
            </button>
          </div>
        </form>
      </div>

      {/* Permits List */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {permits.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-secondary-200">
                <thead className="bg-secondary-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Permit Details
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Area
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Created By
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Issue Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Date Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-secondary-200">
                  {permits.map((permit) => (
                    <tr key={permit.id} className="hover:bg-secondary-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-secondary-900">
                            {permit.permit_no}
                          </div>
                          <div className="text-sm text-secondary-600 truncate max-w-xs">
                            {permit.name}
                          </div>
                          <div className="text-xs text-secondary-500 truncate max-w-xs">
                            {permit.village}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900">
                        {permit.size_sqft}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-secondary-900">
                          {permit.submitter?.fullName || 'N/A'}
                        </div>
                        <div className="text-xs text-secondary-500">
                          {permit.submitter?.username || ''}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900">
                        {permit.issue_date ? formatDate(permit.issue_date) : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`status-badge ${getStatusColor(permit.status)}`}>
                          {getStatusText(permit.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900">
                        {formatDate(permit.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <Link
                          to={`/permits/${permit.id}`}
                          className="text-primary-600 hover:text-primary-900 mr-3"
                        >
                          View
                        </Link>
                        {((permit.status === 'Draft' || permit.status === 'Returned') && permit.submittedBy === user.id) && (
                          <Link
                            to={`/permits/${permit.id}/edit`}
                            className="text-secondary-600 hover:text-secondary-900"
                          >
                            Edit
                          </Link>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-secondary-200">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setPage(page - 1)}
                    disabled={!pagination.hasPrev}
                    className="btn btn-outline disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={!pagination.hasNext}
                    className="btn btn-outline disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-secondary-700">
                      Showing page <span className="font-medium">{pagination.currentPage}</span> of{' '}
                      <span className="font-medium">{pagination.totalPages}</span> ({pagination.totalPermits} total)
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button
                        onClick={() => setPage(page - 1)}
                        disabled={!pagination.hasPrev}
                        className="btn btn-outline rounded-l-md disabled:opacity-50"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => setPage(page + 1)}
                        disabled={!pagination.hasNext}
                        className="btn btn-outline rounded-r-md disabled:opacity-50"
                      >
                        Next
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12">
            <FileText className="mx-auto h-12 w-12 text-secondary-300" />
            <h3 className="mt-2 text-sm font-medium text-secondary-900">No permits found</h3>
            <p className="mt-1 text-sm text-secondary-500">
              {appliedSearch || appliedStatus ? 'Try adjusting your search criteria' : 'Get started by creating a new permit'}
            </p>
            <div className="mt-6">
              <Link to="/permits/new" className="btn btn-primary">
                <Plus className="h-4 w-4 mr-2" />
                New Permit
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PermitList;